import { http } from '@pendo/http';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { postAggregation } from '@pendo/agg-connect';

export function createSubscription (subscription) {
    return http.post('/api/signup/subscription/create', subscription).then((res) => res.data);
}

export function fetchSubscription (subId) {
    return http.get(`/api/s/${subId}/subscription`).then((res) => res.data);
}

export function fetchSetting (setting, abortSignal) {
    return http.get(`/api/s/_SID_/subscription/setting/${setting}`, { signal: abortSignal }).then((res) => {
        if (!res || !res.data) {
            return null;
        }

        return res.data.value;
    });
}

export function updateSetting (setting, value) {
    return http.put(`/api/s/_SID_/subscription/setting/${setting}`, value).then((res) => res.data);
}

export function deleteSetting (setting) {
    return http.delete(`/api/s/_SID_/subscription/setting/${setting}`).then((res) => res.data);
}

export function fetchHasGuides () {
    return http.get('/api/s/_SID_/subscription/hasguides').then((res) => res.data);
}

export function fetchEventCount (subId = null) {
    if (subId !== null) {
        return http.get(`/api/s/${subId}/subscription/eventCount`).then((res) => res.data);
    }

    return http.get('/api/s/_SID_/subscription/eventCount').then((res) => res.data);
}

export function update (subscription) {
    return http.put('/api/s/_SID_/subscription', subscription).then((res) => res.data);
}

export const SUB_PROPERTY_API_BINDING_KEYS = {
    minVisitorsInSegment: 'size',
    maxSessionInactivity: 'duration',
    oauthMaxSessionLength: 'duration',
};

/**
 * Update a specific subscription property according to a path parameter.
 * Useful for when the generic subscription-update API is too heavy-handed,
 * or when the backend implements special validation logic for a specific property.
 */
export function updateByName (property, value) {
    return http.put(`/api/s/_SID_/subscription/${property}`, value).then((res) => res.data);
}

export function updateFlag ({ flag, enabled }) {
    return http.put(`/api/s/_SID_/subscription/flag/${flag}`, { set: enabled }).then((res) => res.data);
}

export function createDiscoveryApplication () {
    return http.post('/api/s/_SID_/application/discovery', {}, { params: { mode: 'async' } }).then((res) => res.data);
}

export function getDiscoveryManifestSyncStatus ({ requestId }) {
    return http.get(`/api/s/_SID_/application/discovery/catalogue/sync/status/${requestId}`).then((res) => res.data);
}

export function updateAutoProvFlag ({ subscriptionId, reason }) {
    return http
        .post(`/api/super/s/${subscriptionId}/subscription/removeAutoProvisioning`, { reason })
        .then((res) => res.data);
}

export function recordRaw () {
    return http.post('/api/s/_SID_/subscription/recordraw', {}).then((res) => res.data);
}

export function startFreeTrial ({ duration = 14, checklist, upfront } = {}) {
    const params = new URLSearchParams({
        action: 'start',
        duration,
    });
    if (checklist) params.set('checklist', checklist);
    if (upfront) params.set('upfront', upfront);

    return http.post(`/api/s/_SID_/trial?${params}`).then((res) => res.data);
}

export function createStripeCustomer () {
    return http.post('/api/s/_SID_/billing/customer');
}

export function createStripeSubscription () {
    return http.post('/api/s/_SID_/billing/subscription');
}

export function getCustomerPortal () {
    return http.get('/api/s/_SID_/billing/portal');
}

export function extendFreeTrial (numDays, subscriptionId) {
    return http
        .post(`/api/super/s/${subscriptionId}/subscription/trial?action=extend&days=${numDays}`, {})
        .then((res) => res.data);
}

export function getTrialHistory () {
    return http.get('/api/s/_SID_/subscription/trial/history').then((res) => {
        if (!res || !res.data) {
            return null;
        }

        return res.data;
    });
}

export function updatePlan (body, query = '') {
    return http.put(`/api/super/s/_SID_/plan${query}`, body).then((res) => res.data);
}

// SCIM
export async function checkScimEnabledForSubscription () {
    try {
        const result = await http.get('/api/s/_SID_/scim/isEnabled');
        if (result?.status === 200) {
            return result.data;
        }
    } catch {
        throw new Error('Failed to retrieve SCIM status for subscription');
    }
}

export async function getSubscriptionVisitorMau ({ mauOnly, showAnonymous, isFY25PricingModel } = {}) {
    const request = {
        response: {
            location: 'request',
            mimeType: 'application/json',
        },
        requests: [
            {
                name: 'MAUCount',
                productArea: PRODUCT_AREAS.ADMIN,
                subProductArea: SUB_PRODUCT_AREAS.MAU,
                pipeline: [
                    {
                        source: {
                            events: { blacklist: 'apply', appId: 'expandAppIds("*")' },
                            timeSeries: { period: 'last30days', first: -1, count: -1 },
                        },
                    },
                    ...(showAnonymous ? [] : [{ identified: 'visitorId' }]),
                    {
                        reduce: {
                            count: {
                                count: mauOnly ? ['visitorId', 'appId'] : ['visitorId'],
                            },
                        },
                    },
                ],
                settings: {
                    mauLimitingBehavior: 'ignore',
                    anonymousMapping: isFY25PricingModel ? 'mapped' : 'unmapped',
                },
            },
        ],
    };

    const result = await postAggregation(request, {});
    const aggRows = result?.data?.messages?.[0]?.rows;
    if (!aggRows || !aggRows.length) return 0; // exit early if rows is empty

    return aggRows.map(({ count }) => count || 0).reduce((a, b) => a + b, 0); // sum together the row counts
}

export async function getSubscriptionPromptCount () {
    // Use the same aggregation as monthly-subscription-prompt-limit.aggregation.ts (fixed: start of calendar month through now)
    const { default: MonthlySubscriptionPromptLimitAggregation } = await import('@/ai-agents/aggregations/monthly-subscription-prompt-limit.aggregation');

    const pipeline = MonthlySubscriptionPromptLimitAggregation.build();

    const request = {
        response: {
            location: 'request',
            mimeType: 'application/json',
        },
        requests: [
            {
                name: MonthlySubscriptionPromptLimitAggregation.name,
                productArea: MonthlySubscriptionPromptLimitAggregation.productArea,
                subProductArea: MonthlySubscriptionPromptLimitAggregation.subProductArea,
                pipeline,
            },
        ],
    };

    const result = await postAggregation(request, {});
    const messages = result?.data?.messages;
    if (!messages || !messages.length) {
        return 0;
    }

    // Time-series agg returns one message per period (e.g. per day); each message has rows with total. Sum across all periods.
    const totalPrompts = messages.reduce((sum, msg) => {
        const rows = msg?.rows || [];

        return sum + rows.reduce((s, row) => s + (row.total || 0), 0);
    }, 0);

    return totalPrompts;
}

export function getOIDCConfiguration () {
    return http.get('/api/adoptmanager/s/_SID_/oidcconfiguration').then((res) => res.data);
}

export function updateOIDCConfiguration (body) {
    return http.put('/api/adoptmanager/s/_SID_/oidcconfiguration', body).then((res) => res.data);
}

export function updateAutoGrantAccessExpiresOn ({ property, value }) {
    return http.put(`/api/s/_SID_/subscription/${property}/${value}`, {}).then((res) => res.data);
}

export function updateSubscriptionExtensionAttributes (extensionAttributes) {
    return http.put('/api/s/_SID_/subscription/extensionAttributes', extensionAttributes).then((res) => res.data);
}

export function updateInsightsFlag (body) {
    return http.post('/api/s/_SID_/insights/activation', body).then((res) => res.data);
}

export function updateSemanticSearchFlag (body) {
    return http.post('/api/s/_SID_/semanticSearch/activation', body).then((res) => res.data);
}

export function fetchAeuSubscriptionsForReseller () {
    return http.get('/api/s/_SID_/subscription/aeusubscriptions').then((res) => res.data);
}

export function updateEmailThrottleSettings (settings) {
    return http.put('/api/s/_SID_/subscription/emailthrottlesettings', settings);
}

export function updateJourneyThrottleSettings (settings) {
    return http.put('/api/s/_SID_/subscription/journeythrottlesettings', settings);
}
